import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { fetcher } from '@dashboard/services/request';
import Button from '@dashboard/components/Button';
import { Button as ShadcnButton } from '@dashboard/components/ui/button';
import Dialog from '@dashboard/components/Dialog';
import { toast } from '@dashboard/context/ToastContext';
import { Form } from '@dashboard/components/Formik';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import SettingsHeader from '@/dashboard/components/SettingsHeader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import Tooltip from '@dashboard/components/Tooltip';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';

const paymentMethodDomainsQueryKey = sellerId => ['seller', sellerId, 'paymentMethodDomains'];

function usePaymentMethods() {
  const { sellerId, token } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: paymentMethodDomainsQueryKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}/payment-method-domains`, token),
  });

  return {
    data,
    isLoading,
    error,
  };
}

export default function PaymentMethodDomains() {
  const { seller, sellerId, token } = useAuth();
  const { data } = usePaymentMethods();
  const queryClient = useQueryClient();

  const addPaymentMethodDomainMutation = useMutation({
    mutationFn: async ({ domainName }) => {
      return fetcher(`/sellers/${sellerId}/payment-method-domains`, token, {
        method: 'POST',
        body: JSON.stringify({ domainName }),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentMethodDomainsQueryKey(sellerId),
      });
    },
  });

  const validatePaymentMethodDomainMutation = useMutation({
    mutationFn: async ({ domainId }) => {
      const response = await fetcher(
        `/sellers/${sellerId}/payment-method-domains/${domainId}/validate`,
        token,
        {
          method: 'POST',
        },
      );

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentMethodDomainsQueryKey(sellerId),
      });

      toast({
        title: 'Domain validated',
      });
    },
    onError: () => {
      toast({
        title: 'Domain verification failed',
        description: 'Please make sure you uploaded the domain verification file and try again.',
        type: 'error',
      });
    },
  });

  const removePaymentMethodDomainMutation = useMutation({
    mutationFn: async ({ domainId }) => {
      return fetcher(`/sellers/${sellerId}/payment-method-domains/${domainId}`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: paymentMethodDomainsQueryKey(sellerId),
      });

      toast({
        title: 'Domain deleted',
      });
    },
    onError: () => {
      toast({
        title: 'Failed to delete domain',
        type: 'error',
      });
    },
  });

  return (
    <div>
      <div className="flex justify-between items-end">
        <SettingsHeader>Domains</SettingsHeader>

        <Dialog
          title={`Add a domain`}
          description={
            <>
              Add the domain (e.g. yourdomain.com) or a subdomain (e.g. shop.yourdomain.com) where
              you will add checkout pages.
            </>
          }
          cancelButton={null}
          confirmButton={null}
          trigger={
            !checkIsConnectedToStripe(seller) ? (
              <Tooltip label="First connect a Stripe account" asChild>
                {/* A disabled button fires no pointer events, so the focusable span carries the tooltip. */}
                <span tabIndex={0} className="inline-flex rounded-md">
                  <Button appearance="primary" disabled>
                    + Add domain
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button appearance="primary">+ Add domain</Button>
            )
          }
          size="xl"
        >
          {({ close }) => (
            <Form
              initialValues={{
                domainName: '',
              }}
              fields={[
                {
                  name: 'domainName',
                  type: 'text',
                  label: 'Domain name',
                  required: true,
                },
              ]}
              onSubmit={async values => {
                try {
                  const { data, error } = await addPaymentMethodDomainMutation.mutateAsync(values);

                  if (error) {
                    throw new Error(error);
                  }

                  close();

                  toast({
                    title: 'Domain added',
                  });

                  return {
                    data,
                  };
                } catch (error) {
                  return {
                    error,
                  };
                }
              }}
            />
          )}
        </Dialog>
      </div>

      <div className="mt-4 border-t border-gray-100 dark:border-border">
        {data?.length === 0 && (
          <div className="text-gray-700 dark:text-muted-foreground py-4 text-sm">
            No domains added yet
          </div>
        )}

        {data?.map(domain => (
          <div
            className="grid grid-cols-12 border-b last:border-0 border-gray-100 dark:border-border py-2 items-center"
            key={domain._id}
          >
            <div className="col-span-11 font-medium text-sm">{domain.domainName}</div>

            <div className="col-span-1 text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <ShadcnButton variant="ghost" size="icon" aria-label="More options">
                    <EllipsisVerticalIcon className="size-4" />
                  </ShadcnButton>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() =>
                      removePaymentMethodDomainMutation.mutate({
                        domainId: domain._id,
                      })
                    }
                    variant="destructive"
                  >
                    Delete domain
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
